import { useState, useEffect } from 'react';
import { useSession } from '../hooks/useSession';
import BeerGlass from '../components/session/BeerGlass';
import BacDisplay from '../components/session/BacDisplay';
import DrinkButtons from '../components/session/DrinkButtons';
import DrinkCatalog from '../components/session/DrinkCatalog';
import DrinkHistory from '../components/session/DrinkHistory';
import ResetButton from '../components/session/ResetButton';
import NotificationToast from '../components/common/NotificationToast';
import NotificationPermissionDialog from '../components/common/NotificationPermissionDialog';

export default function SessionPage() {
  const {
    session,
    addDrink,
    removeDrink,
    resetSession,
    error,
    clearError,
    notificationToast,
    clearNotificationToast,
    requestNotificationPermission
  } = useSession();
  const [showCatalog, setShowCatalog] = useState(false);
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [permissionAskedForSession, setPermissionAskedForSession] = useState<string | null>(null);

  // Show permission dialog on first drink added (once per session)
  useEffect(() => {
    if (session?.drinks.length === 1 && session.sessionId !== permissionAskedForSession) {
      const browserPermission = 'Notification' in window ? Notification.permission : 'denied';

      // Show dialog only if browser permission is still default
      if (browserPermission === 'default') {
        setShowPermissionDialog(true);
      }
    }
  }, [session?.drinks.length, session?.sessionId, permissionAskedForSession]);

  const handleAllowNotifications = () => {
    setPermissionAskedForSession(session?.sessionId ?? null);
    setShowPermissionDialog(false);
    requestNotificationPermission();
  };

  const handleDismissPermissionDialog = () => {
    setPermissionAskedForSession(session?.sessionId ?? null);
    setShowPermissionDialog(false);
  };

  if (!session) {
    return null;
  }

  const handleAddDrink = (templateId: string) => {
    addDrink({ templateId });
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Notification toast */}
      <NotificationToast message={notificationToast} onClose={clearNotificationToast} />

      {/* Error toast */}
      {error && (
        <div className="fixed top-4 left-4 right-4 p-4 bg-red-900/90 border border-red-700 rounded-lg flex items-center justify-between z-40">
          <span className="text-red-200">{error}</span>
          <button
            onClick={clearError}
            className="text-red-400 hover:text-red-200"
          >
            ✕
          </button>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 p-4 space-y-6">
        {/* BAC Display */}
        <BacDisplay
          currentBac={session.currentBac}
          targetBac={session.targetBac}
          trend={session.trend}
        />

        {/* Beer Glass Visualization */}
        <BeerGlass
          currentBac={session.currentBac}
          targetBac={session.targetBac}
        />

        {/* Quick drink buttons */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Přidat nápoj</h2>
            <button
              onClick={() => setShowCatalog(true)}
              className="text-beer hover:text-beer-light text-sm font-medium"
            >
              Více nápojů →
            </button>
          </div>
          <DrinkButtons onAddDrink={handleAddDrink} />
        </div>

        {/* Drink history */}
        <div>
          <h2 className="text-lg font-semibold mb-3">Historie nápojů</h2>
          <DrinkHistory
            drinks={session.drinks}
            onRemoveDrink={removeDrink}
          />
        </div>

        {/* Reset button */}
        <ResetButton onReset={resetSession} />
      </div>

      {/* Drink catalog modal */}
      {showCatalog && (
        <DrinkCatalog
          onAddDrink={handleAddDrink}
          onClose={() => setShowCatalog(false)}
        />
      )}

      {/* Notification permission dialog */}
      {showPermissionDialog && (
        <NotificationPermissionDialog
          onAllow={handleAllowNotifications}
          onDismiss={handleDismissPermissionDialog}
        />
      )}
    </div>
  );
}
